export function usd(v: number | null | undefined, opts: { signed?: boolean; digits?: number } = {}): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  const a = Math.abs(v)
  if (a < 0.5) return '$0'
  const sign = v < 0 ? '−' : opts.signed ? '+' : ''
  const d = opts.digits
  if (a >= 1e12) return `${sign}$${(a / 1e12).toLocaleString('en-US', { maximumFractionDigits: d ?? 2 })}T`
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(d ?? 2)}B`
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(d ?? 1)}M`
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(d ?? 0)}K`
  return `${sign}$${a.toFixed(0)}`
}

export function num(v: number | null | undefined, digits = 0, signed = false): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  const s = Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits })
  return (v < 0 && Math.abs(v) >= 10 ** -digits / 2 ? '−' : signed && v > 0 ? '+' : '') + s
}

export function pct(v: number | null | undefined, digits = 1, signed = false): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '–'
  return `${(v < 0 ? '−' : signed && v > 0 ? '+' : '') + Math.abs(v * 100).toFixed(digits)}%`
}

export function fmt(v: number | null | undefined, format: string, signed = false): string {
  switch (format) {
    case 'usd':
      return usd(v, { signed })
    case 'percent':
      return pct(v, 1, signed)
    case 'fte':
      return num(v, 1, signed)
    default:
      return num(v, Math.abs(v ?? 0) < 10 ? 2 : 0, signed)
  }
}

/** Full-precision numbers for grids: the table shows what the model holds. */
export function cell(v: number | null | undefined, format: string): string {
  if (v === null || v === undefined) return ''
  if (format === 'percent') return pct(v, 2)
  if (format === 'fte') return num(v, 1)
  if (format === 'usd') return num(v, 0)
  return num(v, Math.abs(v) < 10 ? 2 : 0)
}

export function ms(v: number | null | undefined): string {
  if (v === null || v === undefined) return '–'
  return v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${Math.round(v)} ms`
}

export function ago(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`
  return new Date(iso).toLocaleDateString()
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .map(p => p[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/** Is a lower value better for this KPI? (costs and spend) */
export function costLike(key: string, label = ''): boolean {
  return /opex|cost|spend|expense|amount|budget/i.test(`${key} ${label}`) && !/revenue|ebitda|margin|profit/i.test(`${key} ${label}`)
}

/** A calendar date for people: "Oct 9, 2026" from "2026-10-09" (anything else is returned as is). */
export function day(d: string | null | undefined): string {
  if (!d) return ''
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return d
  return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
